/**
 * ArkFloorPlan 布景层（2026-10-18）—— 区域与物件的唯一图形来源（POS 与 Admin 共用）。
 *
 * 条目来自 C# FloorPlanDecorRenderItem（FloorPlanDecorRender.Build 映射好）：
 *   { id, kind, x, y, w, h, rotation, flip, material, label, glyph }
 *   x / y 是中心、w / h 是未旋转宽高（画布像素，口径与桌台相同）；kind 是种类 token；
 *   material 是区域填充的素材档位（同 ark-floor-plan-materials.js）；label 已按语言解析好；
 *   glyph 是图标类的 Lucide 路径（只来自 C# 固定映射，不含用户输入）。
 *
 * 只管画：一个 svg（viewBox = 画布像素），区域在下、其余物件在上，各自按列表顺序叠放。
 * 名称一律 textContent（不经 HTML 解析）。颜色全在 ark-floor-plan.css「布景」段（CSS 变量，浅深两套）。
 * 图标 / 文字 / 柜台名称保持正向：图标类不随旋转转动，文字条目随自身旋转。
 */

import { fillMaterial } from './ark-floor-plan-materials.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

function el(tag, attrs, cls) {
    const e = document.createElementNS(SVG_NS, tag);
    if (attrs) for (const k in attrs) e.setAttribute(k, String(attrs[k]));
    if (cls) e.setAttribute('class', cls);
    return e;
}

const r2 = n => Math.round(n * 100) / 100;

/** 图标类：不随旋转转动（倒着的洗手间图标没有意义） */
const ICON_KINDS = new Set(['restroom', 'kitchen', 'cashier', 'entrance']);

function text(content, attrs, cls) {
    const t = el('text', attrs, cls);
    t.textContent = content;
    return t;
}

/** 各种类在「以中心为原点、未旋转」的 w×h 盒子里画自己 */
const PAINTERS = {
    zone(g, it, ctx) {
        const x = -it.w / 2, y = -it.h / 2;
        if (it.material) {
            const defs = el('defs');
            g.appendChild(defs);
            fillMaterial(g, defs, it.material, `${ctx.prefix}-z-${ctx.seq++}`, x, y, it.w, it.h);
        }
        g.appendChild(el('rect', { x, y, width: it.w, height: it.h }, 'ark-dc-outline'));
        if (it.label) g.appendChild(text(it.label, { x: x + 12, y: y + 26 }, 'ark-dc-label'));
    },

    wall(g, it) {
        g.appendChild(el('rect', { x: -it.w / 2, y: -it.h / 2, width: it.w, height: it.h }, 'ark-dc-wall'));
    },

    // 窗：墙体位置的一段浅色框 + 中线（双线读作玻璃）
    window(g, it) {
        g.appendChild(el('rect', { x: -it.w / 2, y: -it.h / 2, width: it.w, height: it.h }, 'ark-dc-window'));
        const horizontal = it.w >= it.h;
        g.appendChild(el('path', { d: horizontal ? `M${-it.w / 2} 0H${it.w / 2}` : `M0 ${-it.h / 2}V${it.h / 2}` }, 'ark-dc-window-line'));
    },

    // 门：铰链在左下，门扇竖直立起，四分之一虚线弧扫到右下（翻转 = 镜像到右侧铰链）
    door(g, it) {
        const x0 = -it.w / 2, y0 = it.h / 2;
        g.appendChild(el('path', { d: `M${x0} ${y0}H${it.w / 2}` }, 'ark-dc-threshold'));
        g.appendChild(el('path', { d: `M${x0} ${y0}V${y0 - it.h}` }, 'ark-dc-line'));
        g.appendChild(el('path', { d: `M${it.w / 2} ${y0}A${it.w} ${it.h} 0 0 0 ${x0} ${y0 - it.h}` }, 'ark-dc-arc'));
    },

    pillar(g, it) {
        g.appendChild(el('rect', { x: -it.w / 2, y: -it.h / 2, width: it.w, height: it.h, rx: 2 }, 'ark-dc-fx'));
    },

    // 楼梯：外框 + 横向踏步线（14px 一级）+ 中轴向上箭头
    stairs(g, it) {
        const x = -it.w / 2, y = -it.h / 2;
        g.appendChild(el('rect', { x, y, width: it.w, height: it.h }, 'ark-dc-fx'));
        let d = '';
        for (let sy = y + 14; sy < it.h / 2 - 2; sy += 14) d += `M${x} ${r2(sy)}H${it.w / 2}`;
        if (d) g.appendChild(el('path', { d }, 'ark-dc-step'));
        const ah = Math.min(10, it.w / 4);
        g.appendChild(el('path', { d: `M0 ${it.h / 2 - 6}V${y + 6}M${-ah} ${y + 6 + ah}L0 ${y + 6}L${ah} ${y + 6 + ah}` }, 'ark-dc-line'));
    },

    counter(g, it) {
        g.appendChild(el('rect', { x: -it.w / 2, y: -it.h / 2, width: it.w, height: it.h, rx: 4 }, 'ark-dc-fx'));
        if (it.label) g.appendChild(text(it.label, { x: 0, y: 0 }, 'ark-dc-label-c'));
    },

    // 绿植：圆形冠幅 + 三片叶脉
    plant(g, it) {
        const r = Math.min(it.w, it.h) / 2;
        g.appendChild(el('circle', { cx: 0, cy: 0, r }, 'ark-dc-plant'));
        const k = r * 0.6;
        g.appendChild(el('path', { d: `M0 ${r2(k)}V${r2(-k)}M0 ${r2(k * 0.2)}Q${r2(-k)} ${r2(-k * 0.1)} ${r2(-k)} ${r2(-k * 0.7)}M0 ${r2(k * 0.2)}Q${r2(k)} ${r2(-k * 0.1)} ${r2(k)} ${r2(-k * 0.7)}` }, 'ark-dc-leaf'));
    },

    text(g, it) {
        if (!it.label) return;
        g.appendChild(text(it.label, { x: 0, y: 0, 'font-size': r2(Math.max(10, it.h * 0.62)) }, 'ark-dc-text'));
    },
};

/** 图标类：圆角底 + Lucide 字形（24×24 视盒缩放进盒子中央），名称写在盒子下方 */
function paintIcon(g, it) {
    g.appendChild(el('rect', { x: -it.w / 2, y: -it.h / 2, width: it.w, height: it.h, rx: Math.min(8, it.w / 4) }, 'ark-dc-badge'));
    if (it.glyph) {
        const size = Math.min(it.w, it.h) * 0.6;
        const glyph = el('g', { transform: `translate(${r2(-size / 2)} ${r2(-size / 2)}) scale(${r2(size / 24)})` }, 'ark-dc-glyph');
        glyph.innerHTML = it.glyph;   // 只来自 C# 固定映射（LucideIcons），不含用户输入
        g.appendChild(glyph);
    }
    if (it.label) g.appendChild(text(it.label, { x: 0, y: r2(it.h / 2 + 14) }, 'ark-dc-label-s'));
}

/** 单个条目的 <g>：平移到中心、按种类决定是否旋转 / 镜像 */
export function buildDecorItem(it, ctx) {
    const kind = it.kind;
    const transforms = [`translate(${r2(it.x)} ${r2(it.y)})`];
    if (!ICON_KINDS.has(kind)) {
        if (it.rotation) transforms.push(`rotate(${it.rotation})`);
        if (it.flip && kind === 'door') transforms.push('scale(-1 1)');
    }
    const g = el('g', { transform: transforms.join(' '), 'data-id': it.id, 'data-kind': kind }, `ark-dc ark-dc-${kind}`);
    if (ICON_KINDS.has(kind)) paintIcon(g, it);
    else (PAINTERS[kind] || PAINTERS.pillar)(g, it, ctx);
    return g;
}

let _seq = 0;

/** 往 svg 里画全部条目（先清空）：区域组在下，其余在上；各组内按列表顺序 */
export function renderDecor(svg, items, idPrefix = 'ark') {
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const zones = el('g', null, 'ark-dc-zones');
    const things = el('g', null, 'ark-dc-items');
    svg.appendChild(zones);
    svg.appendChild(things);
    const ctx = { prefix: `${idPrefix}-dc${++_seq}`, seq: 0 };
    for (const it of items || []) {
        if (!it || !it.kind) continue;
        (it.kind === 'zone' ? zones : things).appendChild(buildDecorItem(it, ctx));
    }
    return svg;
}

/** 新建布景 svg（viewBox = 画布像素） */
export function createDecorSvg(w, h) {
    return el('svg', { viewBox: `0 0 ${w} ${h}`, width: w, height: h }, 'ark-floor-plan-decor');
}

export default { createDecorSvg, renderDecor, buildDecorItem };
